import { useEffect, useRef, useState } from 'react';
import { Spin } from 'antd';
import {
  QueryBuilder,
  formatQuery,
  type RuleGroupType,
  type RuleType,
} from 'react-querybuilder';
import { QueryBuilderAntD } from '@react-querybuilder/antd';
import 'react-querybuilder/dist/query-builder.css';
import type { CollectionFilterGroupIn } from '@/api/generated/schemas';
import { useFilterFields } from './useFilterFields';
import { DispatchValueEditor } from './valueEditors';

interface Props {
  value?: CollectionFilterGroupIn | null;
  onChange?: (value: CollectionFilterGroupIn | null) => void;
  disabled?: boolean;
}

const toRuleGroup = (
  value?: CollectionFilterGroupIn | null
): RuleGroupType => ({
  combinator: 'and',
  not: false,
  rules: ((value?.rules ?? []) as RuleType[]).map((rule) => ({ ...rule })),
});

/** Empty filter (no rules) maps to NULL in the payload. */
const toFilterGroupIn = (
  query: RuleGroupType
): CollectionFilterGroupIn | null => {
  if (!query.rules || query.rules.length === 0) {
    return null;
  }
  const parsed = JSON.parse(formatQuery(query, 'json_without_ids'));
  return { ...parsed, combinator: 'and' };
};

/**
 * Single-group (no nesting), AND-only query builder bound to a Form.Item.
 * Emits the "JSON, no identifiers" shape consumed by `publication_custom_filters`.
 */
export const CollectionQueryBuilder = ({
  value,
  onChange,
  disabled,
}: Props) => {
  const { fields, isLoading } = useFilterFields();
  const [query, setQuery] = useState<RuleGroupType>(() => toRuleGroup(value));
  // Tracks the last value emitted via onChange, so the sync effect below only
  // rebuilds local state on external changes (late form data, resetFields).
  const lastEmitted = useRef<CollectionFilterGroupIn | null | undefined>(value);

  useEffect(() => {
    if (value !== lastEmitted.current) {
      lastEmitted.current = value;
      setQuery(toRuleGroup(value));
    }
  }, [value]);

  const handleQueryChange = (next: RuleGroupType) => {
    setQuery(next);
    const emitted = toFilterGroupIn(next);
    lastEmitted.current = emitted;
    onChange?.(emitted);
  };

  if (isLoading) {
    return <Spin />;
  }

  return (
    <QueryBuilderAntD>
      <QueryBuilder
        fields={fields}
        query={query}
        onQueryChange={handleQueryChange}
        disabled={disabled}
        listsAsArrays
        showNotToggle={false}
        showCombinatorsBetweenRules={false}
        combinators={[{ name: 'and', label: 'AND' }]}
        controlElements={{
          valueEditor: DispatchValueEditor,
          addGroupAction: () => null,
        }}
      />
    </QueryBuilderAntD>
  );
};
